import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Translate as TranslateIcon,
  Close as CloseIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Supported languages with their codes and names (client-requested order)
const languages = [
  { code: 'en', name: 'English' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ur', name: 'Urdu' },
  { code: 'zh', name: 'Chinese' },
  { code: 'bn', name: 'Bengali (Bangla)' },
  { code: 'hi', name: 'Hindi' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'id', name: 'Indonesian / Malay (Bahasa)' },
  { code: 'tr', name: 'Turkish' },
  { code: 'fa', name: 'Persian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ps', name: 'Pashtu' },
  { code: 'ru', name: 'Russian' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
];

const TranslatorWidget = ({ postContent, postTitle, enabled = true, onTranslationChange }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [translatedTitle, setTranslatedTitle] = useState(null);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(true); // Default to expanded so options are visible
  const [originalContent, setOriginalContent] = useState(null);
  const [originalTitle, setOriginalTitle] = useState(null);

  // Store original content when widget is first used
  useEffect(() => {
    if (postContent && !originalContent) {
      setOriginalContent(postContent);
    }
    if (postTitle && !originalTitle) {
      setOriginalTitle(postTitle);
    }
  }, [postContent, postTitle, originalContent, originalTitle]);

  // Auto-detect source language (assume English for now, can be enhanced)
  const detectSourceLanguage = () => {
    // Default to English, but can be enhanced with language detection
    return 'en';
  };

  const translateText = async (text, targetLang) => {
    if (!text || !text.trim()) return '';
    
    try {
      // Use backend translation API
      const response = await axios.post(`${apiUrl}/translate`, {
        text: text,
        source: detectSourceLanguage(),
        target: targetLang
      });
      
      return response.data.translatedText || text;
    } catch (err) {
      console.error('Translation error:', err);
      // Fallback: return original text if translation fails
      return text;
    }
  };

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    setError('');

    // If English is selected, show original content
    if (newLanguage === 'en') {
      setTranslatedContent(null);
      setTranslatedTitle(null);
      // Notify parent to use original content
      if (onTranslationChange) {
        onTranslationChange({
          title: originalTitle || postTitle,
          content: originalContent || postContent,
          language: 'en'
        });
      }
      return;
    }

    // If we already have translated content for this language, use it
    // (in a real app, you'd cache translations)
    
    setIsTranslating(true);
    
    try {
      // Translate title
      const titleToTranslate = translatedTitle || originalTitle || postTitle;
      const newTranslatedTitle = await translateText(titleToTranslate, newLanguage);
      setTranslatedTitle(newTranslatedTitle);

      // Translate content - preserve HTML structure including images
      const contentToTranslate = translatedContent || originalContent || postContent;
      
      // Parse HTML and preserve structure
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentToTranslate;
      
      // Collect all text nodes while preserving HTML structure
      const textNodes = [];
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      while (node = walker.nextNode()) {
        const text = node.textContent.trim();
        if (text && text.length > 0) {
          textNodes.push({ node, originalText: node.textContent });
        }
      }
      
      // Check total text content length before translating
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      if (textContent.length > 8000) {
        setError('Content is too long to translate. Maximum 8000 characters.');
        setIsTranslating(false);
        return;
      }
      
      // Translate each text node individually to preserve HTML structure
      // This ensures images, links, and all HTML elements remain intact
      for (const { node: textNode, originalText } of textNodes) {
        const text = originalText.trim();
        if (text && text.length > 0) {
          try {
            // Translate this text node
            const translated = await translateText(text, newLanguage);
            // Update the text node with translated content
            // This preserves all HTML structure (images, links, etc.)
            textNode.textContent = translated;
          } catch (err) {
            console.error('Error translating text node:', err);
            // Keep original text if translation fails
          }
        }
      }
      
      // Get the translated HTML with all structure preserved (images, links, formatting, etc.)
      const translatedHTML = tempDiv.innerHTML;
      
      setTranslatedContent(translatedHTML);
      
      // Notify parent component of translated content
      if (onTranslationChange) {
        onTranslationChange({
          title: newTranslatedTitle,
          content: translatedHTML,
          language: newLanguage
        });
      }
      
    } catch (err) {
      console.error('Translation error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to translate content. Please try again.';
      setError(errorMessage);
      // Reset to original on error
      if (onTranslationChange) {
        onTranslationChange({
          title: originalTitle || postTitle,
          content: originalContent || postContent,
          language: 'en'
        });
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded && selectedLanguage !== 'en') {
      // Trigger translation if not English
      handleLanguageChange({ target: { value: selectedLanguage } });
    } else if (!isExpanded && selectedLanguage === 'en') {
      // Reset to original if English
      setTranslatedContent(null);
      setTranslatedTitle(null);
    }
  };

  if (!enabled) {
    return null;
  }

  // Ensure we have content to translate
  if (!postContent && !postTitle) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.1)',
      }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Translate Post
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{ color: 'white' }}
        >
          {isExpanded ? <CloseIcon /> : <TranslateIcon />}
        </IconButton>
      </Box>

      {isExpanded && (
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="language-select-label">Select Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              label="Select Language"
              disabled={isTranslating}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isTranslating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                Translating...
              </Typography>
            </Box>
          )}

          {selectedLanguage !== 'en' && translatedTitle && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> The post content below has been translated to {languages.find(l => l.code === selectedLanguage)?.name || selectedLanguage}.
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TranslatorWidget;
